import Download from '@mui/icons-material/Download'
import { Accordion, AccordionDetails, AccordionSummary, Tooltip } from '@mui/material'
import dayjs from 'dayjs'
import { useState } from 'react'
import { MOMENT_FORMATS, REPORT_DATE_TYPES } from 'src/consumer/constants'
import {
  downloadExcelSheetFromSheetJsWbData,
  isValidInteger,
  isValidNumber,
  getNumberStringWithCommas,
} from 'src/consumer/helpers/utilFunctions'
import { CollapseIcon } from 'src/mentor/components/ActivityPlanComponent/components/styledComponents'
import * as XLSX from 'xlsx'
import { ReportsTableItem } from '../styledComponents'
import { getJsonForExportingExcelData } from '../utils'
import ReportsTableWrapper from './common/ReportsTableWrapper'
import PatientVisitReport from './PatientVisitReport'
import ScreenerBreakUpReport from './screener/ScreenerBreakUpReport'

export function ReportItemDiv({ children }) {
  return <div className="report-item">{children}</div>
}

export function getValidIntegerOrNaForClinicReport(value) {
  return isValidInteger(value) ? getNumberStringWithCommas(value) : 'NA'
}

export function getValidPercentageOrNaForClinicReport(value) {
  return isValidNumber(value) ? `${value}%` : 'NA'
}

export function getPercentageStringInBrackets(value) {
  return isValidNumber(value) ? `(${value}%)` : ''
}

export function renderIntegerDivForClinicReport(value) {
  return <ReportItemDiv>{getValidIntegerOrNaForClinicReport(value)}</ReportItemDiv>
}

export default function OverallReportComponent({
  entityTitle,
  reports: periodWiseReportsForAnEntity,
  reportType,
  screeners,
  downloadButtonRef,
  allClinicLocations,
  defaultExpanded = false,
  accordionOpenAlways = false,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  function getCorrectReportDateString(momentReportDate, momentReportStartDate) {
    // const momentReportDate = dayjs.utc(reportDate)
    if (reportType === REPORT_DATE_TYPES.day) {
      if (momentReportDate.isSame(dayjs(), 'day')) {
        return 'Today'
      }
      if (momentReportDate.isSame(dayjs().subtract(1, 'day'), 'day')) {
        return 'Yesterday'
      }
      return dayjs
        .utc(momentReportDate)
        .format(MOMENT_FORMATS.completeDateDetailsWithMonthShortFormAndDay)
    }
    if (reportType === REPORT_DATE_TYPES.week) {
      const startOfThisWeek = dayjs().startOf('week')
      const endOfThisWeek = dayjs().endOf('week')
      const startOfLastWeek = dayjs().subtract(1, 'week').startOf('week')
      const endOfLastWeek = dayjs().subtract(1, 'week').endOf('week')

      if (momentReportDate.isBetween(startOfThisWeek, endOfThisWeek, null, '[]')) {
        return 'This Week'
      }
      if (momentReportDate.isBetween(startOfLastWeek, endOfLastWeek, null, '[]')) {
        return 'Last Week'
      }

      // Determine which week of the month the date falls into
      const weekOfMonth = Math.ceil(momentReportDate.date() / 7)
      const monthName = momentReportDate.format('MMMM')
      return `${weekOfMonth}th week of ${monthName}`
    }
    if (reportType === REPORT_DATE_TYPES.month) {
      // if (momentReportDate.isSame(dayjs(), 'month')) {
      //   return 'This Month'
      // }
      // if (momentReportDate.isSame(dayjs().subtract(1, 'month'), 'month')) {
      //   return 'Last Month'
      // }
      return dayjs.utc(momentReportDate).format('MMM YYYY')
    }
    if (reportType === REPORT_DATE_TYPES.year) {
      // if (momentReportDate.isSame(dayjs(), 'month')) {
      //   return 'This Month'
      // }
      // if (momentReportDate.isSame(dayjs().subtract(1, 'month'), 'month')) {
      //   return 'Last Month'
      // }
      return dayjs.utc(momentReportDate).format('YYYY')
    }
    if (reportType === 'custom') {
      return `${dayjs.utc(momentReportStartDate).format('DD MMM, YYYY')}-${dayjs
        .utc(momentReportDate)
        .format('DD MMM, YYYY')}`
    }
  }

  function downloadExcelSheet() {
    const workSheetsJson = getJsonForExportingExcelData(
      periodWiseReportsForAnEntity,
      screeners,
      (repEndTime, startTime) =>
        getCorrectReportDateString(dayjs.utc(repEndTime).local(), dayjs.utc(startTime).local()),
      allClinicLocations,
    )

    const workbook = XLSX.utils.book_new()
    Object.values(workSheetsJson).forEach((wbData) => {
      const worksheet = XLSX.utils.json_to_sheet(wbData?.data)
      XLSX.utils.book_append_sheet(workbook, worksheet, wbData?.wbName)
    })

    downloadExcelSheetFromSheetJsWbData({
      workbookData: workbook,
      titleOfSheet: `${entityTitle} Report`,
    })

    // console.log({ sheetRows: sheetRows })
    // downloadExcelSheetFromRows({
    //   rows: sheetRows,
    //   titleOfSheet: '',
    //   workSheetTitle: 'Report Workbook',
    // })
  }

  return (
    <Accordion
      sx={{ background: '#7373731c' }}
      elevation={0}
      expanded={accordionOpenAlways ? accordionOpenAlways : expanded}
      onChange={(e, newVal) => setExpanded(newVal)}
    >
      <AccordionSummary>
        <div className="d-flex align-items-center gap-2 w-100">
          {accordionOpenAlways ? <></> : <CollapseIcon collapsed={!expanded} />}
          <h4 className="m-0 w-100">{entityTitle}</h4>
          <Tooltip title="Download tables in excel">
            <div
              onClick={(e) => {
                e.stopPropagation()
                downloadExcelSheet()
              }}
              ref={(el) =>
                downloadButtonRef ? (downloadButtonRef.current[entityTitle] = el) : null
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                cursor: 'pointer',
                borderRadius: '50%',
                transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              <Download />
            </div>
          </Tooltip>
        </div>
      </AccordionSummary>
      <AccordionDetails sx={{ pb: 3 }}>
        <ReportsTableWrapper defaultExpanded heading="Consultations">
          <div className="min-width-lg">
            <ReportsTableItem header columns={5 + (allClinicLocations?.length || 0)}>
              <div className="reports-header">Report Date</div>
              <div className="reports-header"># Total Consultations</div>
              <div className="reports-header"># New Consultations</div>
              <div className="reports-header"># Repeat Consultations</div>
              <div className="reports-header"># Fee Collected</div>
              {allClinicLocations?.map((location) => (
                <div key={location?.id} className="reports-header">
                  For {location?.title}
                </div>
              ))}
            </ReportsTableItem>
            {periodWiseReportsForAnEntity?.map((report, idx) => (
              <PatientVisitReport
                metricData={report?.metrics?.consultations}
                getCorrectReportDateString={getCorrectReportDateString}
                reportEndTime={report?.reportEndTime}
                reportStartTime={report?.reportStartTime}
                showPaymentRelatedData
                allClinicLocations={allClinicLocations}
                key={idx}
              />
            ))}
          </div>
        </ReportsTableWrapper>
        <ReportsTableWrapper heading="Patient Details">
          <div className="min-width-lg">
            <ReportsTableItem header columns={4}>
              <div className="reports-header">Report Date</div>
              <div className="reports-header"># Patient Visits</div>
              <div className="reports-header"># New Patient Visits</div>
              <div className="reports-header"># Repeat Patient Visits</div>
            </ReportsTableItem>
            {periodWiseReportsForAnEntity?.map((report, idx) => (
              <PatientVisitReport
                metricData={report?.metrics?.patientVisits}
                getCorrectReportDateString={getCorrectReportDateString}
                reportEndTime={report?.reportEndTime}
                reportStartTime={report?.reportStartTime}
                key={idx}
              />
            ))}
          </div>
        </ReportsTableWrapper>
        <ScreenerBreakUpReport
          reports={periodWiseReportsForAnEntity}
          screeners={screeners}
          heading="Pre Screenings"
          metricString="preScreenings"
          getCorrectReportDateString={getCorrectReportDateString}
        />
        <ScreenerBreakUpReport
          reports={periodWiseReportsForAnEntity}
          screeners={screeners}
          heading="Post Screenings"
          metricString="postScreenings"
          getCorrectReportDateString={getCorrectReportDateString}
        />
        <ReportsTableWrapper heading="Therapy Details">
          <div className="min-width-lg">
            <ReportsTableItem header columns={4}>
              <div className="reports-header">Report Date</div>
              <div className="reports-header"># Therapies</div>
              <div className="reports-header"># New Therapies</div>
              <div className="reports-header"># Existing Therapies</div>
            </ReportsTableItem>
            {periodWiseReportsForAnEntity?.map((report, idx) => (
              <PatientVisitReport
                metricData={report?.metrics?.therapies}
                getCorrectReportDateString={getCorrectReportDateString}
                reportEndTime={report?.reportEndTime}
                reportStartTime={report?.reportStartTime}
                key={idx}
              />
            ))}
          </div>
        </ReportsTableWrapper>
        <ScreenerBreakUpReport
          reports={periodWiseReportsForAnEntity}
          screeners={screeners}
          heading="Therapy/Screening Details"
          metricString="therapyPerScreening"
          getCorrectReportDateString={getCorrectReportDateString}
        />
      </AccordionDetails>
    </Accordion>
  )
}
